import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { date, money } from "@/lib/format";
import type { AccountSummary, Paged } from "@/lib/types";
import { Badge, Card, PageHeader, Pager, Skeleton, Table, cx } from "@/components/ui";

const STATUSES = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Closed", value: "closed" },
];

const PAGE_SIZE = 25;

export function PlatformAccounts() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);

  const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (search.trim()) query.set("q", search.trim());
  if (status) query.set("status", status);

  const { data, isLoading } = useQuery({
    queryKey: ["platform-accounts", search, status, offset],
    queryFn: () => api.get<Paged<AccountSummary>>(`/v1/admin/accounts?${query.toString()}`),
    placeholderData: keepPreviousData,
  });

  const total = data?.meta.total ?? 0;

  return (
    <>
      <PageHeader title="Accounts" subtitle={total ? `${total} merchant accounts` : undefined} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUSES.map((option) => (
          <button
            key={option.label}
            onClick={() => {
              setStatus(option.value);
              setOffset(0);
            }}
            className={cx(
              "rounded-lg px-3 py-1.5 text-sm font-medium transition",
              status === option.value ? "bg-ink-900 text-white" : "bg-white text-ink-600 hover:bg-ink-100",
            )}
          >
            {option.label}
          </button>
        ))}
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
          placeholder="Company or billing email"
          aria-label="Search accounts"
          className="ws-input ml-auto w-full sm:w-80"
        />
      </div>

      {isLoading ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : (
        <>
          <Table head={["Account", "Owner", "Status", "Users", "Shipments", "30-day spend", "Joined"]}>
            {(data?.data ?? []).map((account) => (
              <tr key={account.id} className="transition hover:bg-ink-50">
                <td className="ws-td">
                  <Link to={`/app/admin/accounts/${account.id}/wallet`} className="font-semibold text-brand">
                    {account.trading_name || account.legal_name}
                  </Link>
                  <p className="text-xs text-ink-500">{account.billing_email}</p>
                </td>
                <td className="ws-td text-ink-600">{account.owner_email || "-"}</td>
                <td className="ws-td">
                  <Badge
                    tone={
                      account.status === "active" ? "good" : account.status === "suspended" ? "bad" : "neutral"
                    }
                  >
                    {account.status}
                  </Badge>
                </td>
                <td className="ws-td">{account.users}</td>
                <td className="ws-td">{account.shipments}</td>
                <td className="ws-td font-medium">{money(account.spend_30d_cents)}</td>
                <td className="ws-td text-ink-500">{date(account.created_at)}</td>
              </tr>
            ))}
          </Table>
          <Pager total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
        </>
      )}
    </>
  );
}
